import uuid from "react-uuid";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import sma from 'sma';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormLabel, Grid, Input, Slider, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Box } from "@mui/system";

import { Check, Delete, Restore, UploadFile } from "@mui/icons-material";
import { parseFbaCsv } from "../../helpers/parseFbaCsv";
import { parseFbaPAS } from "../../helpers/parseFbaPAS";
import { defaultSettings } from "./fbaDefaultSettings";
import { selectFBA, updateFBA } from "../../reducers/fbaSlice";
import { useTheme } from "@mui/styles";
import { useSocket } from "../../context/useSocket";
import { addProject, selectProjects, updateProject } from "../../reducers/projectSlice";
import { FBAEditProductionDataChart } from "./FBAEditProductionDataChart";
import { chartColors } from "../../helpers/chartColors";
import { removeOutliersFBAData } from "../../helpers/removeOutliersFBAData";
import { FBASurveyDataTable } from "./FBASurveyDataTable";
import { parseFbaSurveyCsv } from "../../helpers/parseFbaSurveyCsv";
import { FBAProductionDataTable } from "./FBAProductionDataTable";
import { fbaConversionFactors } from "./fbaConversionFactors";
import { f_TO_C } from "../../helpers/conversionFactors";


export const EditFBA = ({ fbaId, setEditFBAVisible, email, darkMode }) => {
  
  const theme = useTheme();
  const projects = useSelector(selectProjects);
  const fba = useSelector(selectFBA);
  const currentFBA = fba?.byId?.[fbaId];

  const [fbaName, setFBAName] = useState(currentFBA?.name || "New FBA");
  const [fbaDescription, setFBADescription] = useState(currentFBA?.description || "New FBA");
  const [fbaData, setFBAData] = useState(currentFBA?.fbaRawData || currentFBA?.fbaData || null);
  const [fbaUnits, setFBAUnits] = useState(currentFBA?.settings?.units?.value || 'field');
  const [fbaWellType, setFBAWellType] = useState("tight oil"); // 1 = tight oil, 2 = tight gas
  const [fbaProcessedData, setFBAProcessedData] = useState(currentFBA?.fbaData || null);
  const [fbaProductionFileName, setFBAProductionFileName] = useState(currentFBA?.fbaProductionFileName || null);
  const [fbaProductionFileSize, setFBAProductionFileSize] = useState(currentFBA?.fbaProductionFileSize || null);
  const [fbaProductionFileUnits, setFBAProductionFileUnits] = useState("metric");
  const [fbaProductionFilePressureType, setFBAProductionFilePressureType] = useState("casing");
  const [fbaSurveyData, setFBASurveyData] = useState(currentFBA?.fbaSurveyData || null);
  const [fbaSurveyFileName, setFBASurveyFileName] = useState(currentFBA?.fbaSurveyFileName || null);
  const [fbaSurveyFileSize, setFBASurveyFileSize] = useState(currentFBA?.fbaSurveyFileSize || null);
  const [fbaSurveyFileUnits, setFBASurveyFileUnits] = useState("metric");
  const [fbaFracType, setFBAFracType] = useState(1); // 1 = unpropped, 2 = propped
  const [tabItem, setTabItem] = useState(0);
  const [fbaVisibility, setFBAVisibility] = useState(currentFBA?.public ? "public" : "private");
  const dispatch = useDispatch();

  const { socket } = useSocket();

  return (
    <Dialog 
      open={true} 
      onClose={() => setEditFBAVisible(false)}
      fullWidth={true}
      maxWidth="lg"
      PaperProps={{
        style: {
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        },
      }}
      elevation={0}
    >
      <DialogTitle>{currentFBA ? "Edit" : "New"} FBA</DialogTitle>
      <DialogContent>
        
        <Tabs
          value={tabItem}
          textColor={darkMode ? "inherit" : "primary"}
          onChange={handleTabChange}
          style={{borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`}}
          TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
        >
          <Tab label={'General'} key={'general'} />
          <Tab label={'Production Data'} key={'production'} />
          <Tab label={'Survey Data'} key={'survey'} />
        </Tabs>
               

        {tabItem === 0 ? 
          <FBAEditGeneral 
            fbaName={fbaName} 
            setFBAName={setFBAName}
            fbaDescription={fbaDescription}
            setFBADescription={setFBADescription}
            fbaVisibility={fbaVisibility}
            setFBAVisibility={setFBAVisibility}
            fbaUnits={fbaUnits}
            setFBAUnits={setFBAUnits}
            fbaWellType={fbaWellType}
            setFBAWellType={setFBAWellType}
            isNewFBA={currentFBA ? false : true}
          /> :
        tabItem === 1 ? 
          <FBAEditProductionData 
            fbaData={fbaData}
            setFBAData={setFBAData}
            fbaProcessedData={fbaProcessedData}
            setFBAProcessedData={setFBAProcessedData}
            fbaProductionFileUnits={fbaProductionFileUnits}
            setFBAProductionFileUnits={setFBAProductionFileUnits}
            fbaProductionFilePressureType={fbaProductionFilePressureType}
            setFBAProductionFilePressureType={setFBAProductionFilePressureType}
            fbaProductionFileName={fbaProductionFileName}
            setFBAProductionFileName={setFBAProductionFileName}
            fbaProductionFileSize={fbaProductionFileSize}
            setFBAProductionFileSize={setFBAProductionFileSize}
            fbaFracType={fbaFracType}
            setFBAFracType={setFBAFracType}
            fbaWellType={fbaWellType}
            darkMode={darkMode}
            theme={theme}
            dispatch={dispatch}
          /> :
          <FBAEditSurveyData 
            fbaSurveyData={fbaSurveyData}
            setFBASurveyData={setFBASurveyData}
            fbaSurveyFileUnits={fbaSurveyFileUnits}
            setFBASurveyFileUnits={setFBASurveyFileUnits}
            fbaSurveyFileName={fbaSurveyFileName}
            setFBASurveyFileName={setFBASurveyFileName}
            fbaSurveyFileSize={fbaSurveyFileSize}
            setFBASurveyFileSize={setFBASurveyFileSize}
          />
        }
        
      </DialogContent>
      <DialogActions style={{display: 'flex', justifyContent: 'space-between'}}>
        {(fbaId && fbaId?.length > 0) ?
        <Button onClick={(e) => handleDeleteFBA(e, fbaId)} color="secondary">
          Delete
        </Button> : <div></div>}
        <div>
          <Button onClick={() => setEditFBAVisible(false)} color={darkMode ? "inherit" : "primary"} style={{marginRight: 10}}>
            Cancel
          </Button>
          <Button onClick={() => handleFBACreateSave(currentFBA)} color={darkMode ? "inherit" : "primary"}
              style={{color: darkMode ? '#000' : '#fff'}} variant="contained" disabled={!fbaData || fbaData.length === 0}>
            {(fbaData && currentFBA?.id) ? "Update" : "Create"}
          </Button>
        </div>  
      </DialogActions>
    </Dialog>
  );

  // FUNCTIONS

  async function handleFBACreateSave(currentFBA) {
    try {
      let newFba = {};
      if (currentFBA) { // update existing FBA if provided
        newFba = {
          ...currentFBA,
          name: fbaName,
          description: fbaDescription,
          lastModified: new Date(),
          fbaData: fbaProcessedData,
          fbaRawData: fbaData,
          fbaProductionFileName,
          fbaProductionFileSize,
          fbaProductionFileUnits,
          fbaSurveyData,
          fbaSurveyFileName,
          fbaSurveyFileSize,
          fbaSurveyFileUnits,
          public: fbaVisibility === 'public',
          projectId: projects?.currentProjectId
        };
      } else { // create new FBA with new id
        const id = uuid();
        const newSettings = JSON.parse(JSON.stringify(defaultSettings));

        newSettings.fracType.value = fbaFracType;
        newSettings.units.value = fbaUnits;
        newSettings.type.value = fbaWellType === 'tight oil' ? 1 : 2;

        if (fbaFracType === 1) { // propped
          newSettings.c_f.value = defaultSettings?.c_f?.value;
          newSettings.phi_f.value = defaultSettings?.phi_f?.value;
          newSettings.w_f.value = defaultSettings?.w_f?.value;
        } else if (fbaFracType === 2) { // unpropped
          newSettings.c_f.value = defaultSettings?.c_f_unpropped?.value;
          newSettings.phi_f.value = defaultSettings?.phi_f_unpropped?.value;
          newSettings.w_f.value = defaultSettings?.w_f_unpropped?.value;
        };


        // convert default values from field to metric units
        if (newSettings?.units?.value === 'metric') {
          for (const param of Object.keys(newSettings)) {
            let value = newSettings?.[param]?.value;

            if (!value) continue;

            if (param === 't_r' || param === 'wht' || param === 'tSep') {
              value = parseInt(f_TO_C(value));
            } else if (param === 'geothermalGradient') {
              value = +(((value) * (5 / 9) * 3.28)?.toFixed(3));
            }  else {
              if (!fbaConversionFactors?.[param]) continue;
              value = newSettings?.[param]?.value * fbaConversionFactors?.[param];
            };

            newSettings[param] = {...newSettings?.[param], value};
          };
        };


        newFba = {
          id, 
          name: fbaName,
          description: fbaDescription,
          createdBy: email,
          color: chartColors[0],
          lastModified: new Date(),
          location: '',
          settings: newSettings,
          fbaData: fbaProcessedData,
          fbaRawData: fbaData,
          fbaProductionFileName,
          fbaProductionFileSize,
          fbaProductionFileUnits,
          fbaSurveyData,
          fbaSurveyFileName,
          fbaSurveyFileSize,
          fbaSurveyFileUnits,
          public: fbaVisibility === 'public',
          projectId: projects?.currentProjectId
        };
      };
      if (!newFba.fbaSurveyData) { // delete wellbore model if survey data is deleted
        delete newFba.fbaWellboreModel;
      };
  
      dispatch(updateFBA({fba: newFba, fbaId: newFba.id}));

      // update project with new FBA id
      const currentProject = {...projects?.byId?.[newFba?.projectId]};

      if (!currentProject.fbaIds.includes(newFba.id)) {
        if (!currentProject.fbaIds) currentProject.fbaIds = [];
        currentProject.fbaIds = [...currentProject.fbaIds, newFba.id];
      };
      dispatch(updateProject({ project: currentProject, projectId: currentProject.id }));


      setEditFBAVisible(false);
      
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDeleteFBA(e, fbaId) {
    e.stopPropagation();

    const currentProject = {...projects?.byId?.[currentFBA?.projectId]};

    currentProject.fbaIds = currentProject?.fbaIds?.filter(id => id !== fbaId);
    dispatch(addProject({ project: currentProject, lists: [], email: email }));

    if (socket && socket.readyState === 1) {
      socket.send(JSON.stringify({messageType: 'deleteFBA', fbaId, userEmail: email}));
    };
    setEditFBAVisible(false);
  };


  function handleTabChange(e, value) {
    setTabItem(value);
  };

};

function FBAEditGeneral({ fbaName, setFBAName, fbaDescription, setFBADescription, fbaVisibility, setFBAVisibility, fbaUnits, setFBAUnits, fbaWellType, setFBAWellType, isNewFBA, darkMode}) {
  return (
    <div style={{display: 'flex', flexDirection: 'column'}}>
      <TextField
        autoFocus
        margin="normal"
        id="name"
        label="FBA Name"
        placeholder="FBA Name"
        type="text"
        fullWidth
        value={fbaName}
        variant="outlined"
        onChange={e => setFBAName(e.target.value)}
      />

      <TextField
        margin="normal"
        id="description"
        label="Description"
        placeholder="Description"
        type="text"
        fullWidth
        multiline
        minRows={2}
        maxRows={5}
        value={fbaDescription}
        variant="outlined"
        onChange={e => setFBADescription(e.target.value)}
      />

      <FormControl component="fieldset" style={{width: '100%', marginTop: 20, marginBottom: 0}}>
        <FormLabel component="legend">Visibility and Access</FormLabel>
        <ToggleButtonGroup
          value={fbaVisibility}
          exclusive
          onChange={(e, val) => val && setFBAVisibility(val)}
          style={{width: '100%', marginTop: 10, height: 30}}
          size="small"
        >
          <ToggleButton value="private" style={{width: '50%'}} color="primary">
            Private
          </ToggleButton>
          <ToggleButton value="public" style={{width: '50%'}} color="primary">
            Public
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>

      {isNewFBA && <FormControl component="fieldset" style={{width: '100%', marginTop: 20}}>
        <FormLabel component="legend">Analysis Units</FormLabel>
        <ToggleButtonGroup
          value={fbaUnits}
          exclusive
          onChange={(e, val) => val && setFBAUnits(val)}
          style={{width: '100%', marginTop: 10, height: 30}}
          size="small"
        >
          <ToggleButton value="field" style={{width: '50%'}} color="primary">
            Field
          </ToggleButton>
          <ToggleButton value="metric" style={{width: '50%'}} color="primary">
            Metric
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>}

      <FormControl component="fieldset" style={{width: '100%', marginTop: 20, marginBottom: 20}}>
        <FormLabel component="legend">Well Type</FormLabel>
        <ToggleButtonGroup
          value={fbaWellType}
          exclusive
          onChange={(e, val) => val && setFBAWellType(val)}
          size="small"
          style={{height: 30, display: 'flex', justifyContent: 'center'}}
        >
          <ToggleButton value="tight oil" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
            <Typography style={{fontSize: 12}}>
              Tight Oil
            </Typography>
          </ToggleButton>
          <ToggleButton value="tight gas" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
            <Typography style={{fontSize: 12}}>
              Tight Gas
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
    </div>
  );
};

function FBAEditProductionData({ 
  fbaData, 
  setFBAData,
  fbaProcessedData, 
  setFBAProcessedData,
  fbaProductionFileUnits,
  setFBAProductionFileUnits,
  fbaProductionFileName,
  setFBAProductionFileName,
  fbaProductionFileSize,
  setFBAProductionFileSize,
  fbaProductionFilePressureType,
  setFBAProductionFilePressureType,
  fbaFracType,
  setFBAFracType,
  fbaWellType,
  darkMode,
  theme,
  dispatch
}) {

  const [fbaPreviewData, setFBAPreviewData] = useState([]);
  const [smoothingWindow, setSmoothingWindow] = useState(0);
  const [despikingSensitivity, setDespikingSensitivity] = useState(0);
  const [despikingWindow, setDespikingWindow] = useState(0);
  const [lofKNeighbors, setLOFKNeighbors] = useState(0);
  const [lofCutoff, setLOFCutoff] = useState(0);
  const [mmrKNeighbors, setMMRKNeighbors] = useState(0);
  const [mmrCutoff, setMMRCutoff] = useState(0);
  const [interpolationFrequency, setInterpolationFrequency] = useState(0);


  if (!fbaData) {
    return (
      <Box style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%', padding: '0 10px', marginTop: 10}}>
          <FormControl component="fieldset" style={{width: '100%', marginBottom: 20}}>
            <FormLabel component="legend">Production Data Units</FormLabel>
            <ToggleButtonGroup
              value={fbaProductionFileUnits}
              exclusive
              onChange={handleProductionFileUnitsChange}
              size="small"
              style={{height: 30, display: 'flex', justifyContent: 'center'}}
            >
              <ToggleButton value="metric" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
                <Typography style={{fontSize: 12}}>
                  Metric
                </Typography>
              </ToggleButton>
              <ToggleButton value="field" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
                <Typography style={{fontSize: 12}}>
                  Field
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <FormControl component="fieldset" style={{width: '100%', marginBottom: 20}}>
            <FormLabel component="legend">Pressure Type</FormLabel>
            <ToggleButtonGroup
              value={fbaProductionFilePressureType}
              exclusive
              onChange={handleProductionFilePressureTypeChange}
              size="small"
              style={{height: 30, display: 'flex', justifyContent: 'center'}}
            >
              <ToggleButton value="casing" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
                <Typography style={{fontSize: 12}}>
                  Casing
                </Typography>
              </ToggleButton>
              <ToggleButton value="tubing" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
                <Typography style={{fontSize: 12}}>
                  Tubing
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          <FormControl component="fieldset" style={{width: '100%', marginBottom: 10}}>
            <FormLabel component="legend">Frac Type</FormLabel>
            <ToggleButtonGroup
              value={fbaFracType}
              exclusive
              onChange={handleFracTypeChange}
              size="small"
              style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center'}}
            >
              <ToggleButton value={1} size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Propped
                </Typography>
              </ToggleButton>
              <ToggleButton value={2} size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
                <Typography style={{fontSize: 12}}>
                  Unpropped
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
        </div>

        <div style={{width: 'calc(100% - 20px)', height: 100, marginTop: 20, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
          <Button
            component="label"
            variant="contained" 
            color={(darkMode ? "inherit" : "primary")}
            style={{color: (darkMode ? '#000' : '#fff'), height: 28, marginBottom: 5}} 
            startIcon={<UploadFile />}
            size="small"
          >
            Upload Production Data
            <input
              type="file"
              accept=".csv, .pas, .PAS"
              hidden
              onChange={e => handleUploadFBAData(e.target.files)}
            />
          </Button>
          <Typography textAlign="center" variant="caption">Select a CSV or PAS file</Typography>
        </div>
      </Box>
    )
  } else {
    return (
      <Box>
        <div style={{width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 10}}>
          <div style={{width: '30%', position: 'relative'}}>
            <Typography variant="h6">Production Data Options</Typography>
            
            <Typography fontSize={16} style={{marginTop: 10}}>Data Augmentation</Typography>
            
            <div style={{height: 'auto', overflow: 'auto', padding: '0 5px 10px 5px'}}>
              
              <Typography color="textSecondary" fontSize={12}>
                DE-SPIKING
              </Typography>
              <div style={{marginLeft: 20, marginTop: 5}}>
                <Typography fontSize={14} gutterBottom noWrap>
                  De-spiking Sensitivity
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={despikingSensitivity}
                      onChange={(e, newValue) => {
                        const newDespikingSensitivity = parseFloat(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, newDespikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setDespikingSensitivity(newDespikingSensitivity);
                      }}
                      min={0}
                      max={10}
                      step={0.1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={despikingSensitivity}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newDespikingSensitivity = parseFloat(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, newDespikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setDespikingSensitivity(newDespikingSensitivity)
                      }}
                      inputProps={{
                        min: 0,
                        max: 10,
                        step: 0.1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
              <div style={{marginLeft: 20, marginTop: 5}}>
                <Typography fontSize={14} gutterBottom noWrap>
                  De-spiking Window
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={despikingWindow}
                      onChange={(e, newValue) => {
                        const newDespikingWindow = parseInt(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, newDespikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setDespikingWindow(newDespikingWindow);
                      }}
                      min={0}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={despikingWindow}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newDespikingWindow = parseInt(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, newDespikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setDespikingWindow(newDespikingWindow);
                      }}
                      inputProps={{
                        min: 0,
                        max: 10,
                        step: 1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>


              <Typography color="textSecondary" fontSize={12}>
                OUTLIER DETECTION
              </Typography>
              <div style={{marginLeft: 20, marginTop: 5}}>

                <Typography fontSize={14} gutterBottom noWrap>
                  LOF k Neighbors
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={lofKNeighbors}
                      onChange={(e, newValue) => {
                        const newLOFKNeighbors = parseInt(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, newLOFKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setLOFKNeighbors(newLOFKNeighbors);
                      }}
                      min={0}
                      max={20}
                      step={1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={lofKNeighbors}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newLOFKNeighbors = parseInt(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, newLOFKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setLOFKNeighbors(newLOFKNeighbors);
                      }}
                      inputProps={{
                        min: 0,
                        max: 20,
                        step: 1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
              <div style={{marginLeft: 20, marginTop: 5}}>

                <Typography fontSize={14} gutterBottom noWrap>
                  LOF Cutoff
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={lofCutoff}
                      onChange={(e, newValue) => {
                        const newLOFCutoff = Number(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, newLOFCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setLOFCutoff(newLOFCutoff);
                      }}
                      min={0}
                      max={2}
                      step={0.01}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={lofCutoff}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newLOFCutoff = Number(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, newLOFCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setLOFCutoff(newLOFCutoff);
                      }}
                      inputProps={{
                        min: 0,
                        max: 2,
                        step: 0.01,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
              <div style={{marginLeft: 20, marginTop: 5}}>

                <Typography fontSize={14} gutterBottom noWrap>
                  MMR k Neighbors
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={mmrKNeighbors}
                      onChange={(e, newValue) => {
                        const newMMRKNeighbors = parseInt(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, newMMRKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setMMRKNeighbors(newMMRKNeighbors);
                      }}
                      min={0}
                      max={20}
                      step={1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={mmrKNeighbors}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newMMRKNeighbors = parseInt(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, newMMRKNeighbors, mmrCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setMMRKNeighbors(newMMRKNeighbors);
                      }}
                      inputProps={{
                        min: 0,
                        max: 10,
                        step: 1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
              <div style={{marginLeft: 20, marginTop: 5}}>
                <Typography fontSize={14} gutterBottom noWrap>
                  MMR Cutoff
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={mmrCutoff}
                      onChange={(e, newValue) => {
                        const newMMRCutoff = Number(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, newMMRCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setMMRCutoff(newMMRCutoff);
                      }}
                      min={0}
                      max={4}
                      step={0.1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={mmrCutoff}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newMMRCutoff = Number(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, newMMRCutoff);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setMMRCutoff(newMMRCutoff);
                      }}
                      inputProps={{
                        min: 0,
                        max: 4,
                        step: 0.1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>

              <Typography color="textSecondary" fontSize={12}>
                SMOOTHING
              </Typography>
              <div style={{marginLeft: 20, marginTop: 5}}>

                <Typography fontSize={14} gutterBottom noWrap>
                  Smoothing Window
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={smoothingWindow}
                      onChange={(e, newValue) => {
                        const newSmoothingWindow = parseInt(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applySmoothing(newPreviewData, newSmoothingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setSmoothingWindow(() => newSmoothingWindow);
                      }}
                      min={0}
                      max={20}
                      step={1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={smoothingWindow}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newSmoothingWindow = parseInt(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applySmoothing(newPreviewData, newSmoothingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applyInterpolation(newPreviewData, interpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setSmoothingWindow(() => newSmoothingWindow);
                      }}
                      inputProps={{
                        min: 0,
                        max: 20,
                        step: 1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            </div>

            
            <Divider />


            <Typography fontSize={16} style={{marginTop: 10}}>Data Resampling</Typography>
            <div style={{height: 'auto', overflow: 'auto', marginTop: 0, padding: '0 5px 10px 5px'}}>
              <Typography color="textSecondary" fontSize={12}>
                INTERPOLATION
              </Typography>
              <div style={{marginLeft: 20, marginTop: 5}}>
                
                <Typography fontSize={14} gutterBottom noWrap>
                  Interpolation Frequency
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs style={{margin: '0 10px'}}>
                    <Slider
                      value={interpolationFrequency}
                      onChange={(e, newValue) => {
                        const newInterpolationFrequency = parseInt(newValue);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applyInterpolation(newPreviewData, newInterpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setInterpolationFrequency(newInterpolationFrequency);
                      }}
                      min={0}
                      max={4}
                      step={1}
                      valueLabelDisplay="auto"
                      style={{color: (darkMode ? '#fff' : '#0758B7')}}
                    />
                  </Grid>
                  <Grid item>
                    <Input
                      value={interpolationFrequency}
                      margin="dense"
                      style={{width: 60}}
                      onChange={(e) => {
                        const newInterpolationFrequency = parseInt(e.target.value);
                        let newPreviewData = applyDespiking(fbaProcessedData, despikingSensitivity, despikingWindow);
                        newPreviewData = applySmoothing(newPreviewData, smoothingWindow);
                        newPreviewData = applyRemoveOutliers(newPreviewData, lofKNeighbors, lofCutoff, mmrKNeighbors, mmrCutoff);
                        newPreviewData = applyInterpolation(newPreviewData, newInterpolationFrequency);
                        newPreviewData = recalculateFluidRatios(newPreviewData);
                        setFBAPreviewData([...newPreviewData]);
                        setInterpolationFrequency(newInterpolationFrequency);
                      }}
                      inputProps={{
                        min: 0,
                        max: 4,
                        step: 1,
                        type: 'number'
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            </div>

            <Divider />

            <div style={{width: '100%', display: 'flex', marginTop: 20, justifyContent: 'space-between'}}>
              <Button 
                startIcon={<Delete fontSize="20px" />} 
                onClick={() => {
                  setFBAData(null);
                  setFBAProcessedData(null);
                  setFBAPreviewData([]);
                  resetAugmentationParameters();
                }}
                variant="contained" 
                color={(darkMode ? "inherit" : "secondary")}
                style={{color: (darkMode ? '#000' : '#fff'), height: 28}}
                size="small"
              >
                Delete Data
              </Button>
              <Button 
                startIcon={<Restore fontSize="20px" />} 
                onClick={() => {
                  setFBAProcessedData(() => deepCopy(fbaData));
                  setFBAPreviewData([]);
                  resetAugmentationParameters();
                }}
                variant="outlined" 
                style={{height: 28}}
                size="small"
              >
                Reset
              </Button>
              <Button 
                startIcon={<Check fontSize="20px" />} 
                onClick={() => {
                  setFBAProcessedData(() => deepCopy(fbaPreviewData));
                  setFBAPreviewData([]);
                  resetAugmentationParameters();
                }}
                variant="contained" 
                color={(darkMode ? "inherit" : "primary")}
                style={{color: (darkMode ? '#000' : '#fff'), height: 28}}
                size="small"
              >
                Apply
              </Button>
            </div>           
          </div>

          <div style={{width: '68%', display: 'flex', flexDirection: 'column', marginTop: 20}}>
            
            <div>
              <Typography fontSize={12}>File: {fbaProductionFileName || ""}</Typography>
              <Typography fontSize={12}>Size: {fbaProductionFileSize?.toFixed(1) || 0} kb</Typography>
              <Typography fontSize={12}>Rows: {fbaData?.length || 0}</Typography>
              <Typography fontSize={12}>Units: {fbaProductionFileUnits?.charAt(0)?.toUpperCase()?.concat(fbaProductionFileUnits?.substr(1, ))}</Typography>
              <Typography fontSize={12}>Well Type: {fbaWellType?.charAt(0)?.toUpperCase()?.concat(fbaWellType?.substr(1, ))}</Typography>
              <Typography fontSize={12}>Pressure Type: {fbaProductionFilePressureType?.charAt(0)?.toUpperCase()?.concat(fbaProductionFilePressureType?.substr(1, ))}</Typography>
            </div>
            
            <div style={{ height: 400 }}>
              <FBAEditProductionDataChart 
                fbaData={fbaData}
                fbaProcessedData={fbaProcessedData}
                fbaPreviewData={fbaPreviewData}
                currentTrace={'fluids'} 
                darkMode={darkMode} 
                units={fbaProductionFileUnits}
                showRawData={true}
              />
            </div>

            <div style={{ height: 400 }}>
              <FBAEditProductionDataChart 
                fbaData={fbaData}
                fbaProcessedData={fbaProcessedData}
                fbaPreviewData={fbaPreviewData}
                currentTrace={'pressure'} 
                darkMode={darkMode} 
                units={fbaProductionFileUnits}
                showRawData={true}
              />
            </div>
          </div>
        </div>
     
        <FBAProductionDataTable fbaData={fbaProcessedData} units={fbaProductionFileUnits} />
        {/* <FBAProductionDataSpreadsheet fbaProcessedData={fbaProcessedData} setFBAProcessedData={setFBAProcessedData} units={fbaProductionFileUnits} /> */}

      </Box>
    )
  };


  async function handleUploadFBAData(inputData) {
    try {
      if (inputData) {

        const fileType = inputData?.[0]?.name?.split('.')?.slice(-1)?.[0]?.toLowerCase();

        if (fileType === 'csv') {
          const {rawData, fileName, fileSize} = await parseFbaCsv(inputData?.[0], fbaProductionFileUnits);
          if (rawData?.length > 0) {
            setFBAData(rawData);
            setFBAProcessedData(rawData);
            setFBAProductionFileName(fileName);
            setFBAProductionFileSize(fileSize);
          };
        } else if (fileType === 'pas') {
          console.log('Processing PAS file');

          const {rawData, fileName, fileSize} = await parseFbaPAS(inputData?.[0], fbaProductionFilePressureType, fbaWellType, fbaProductionFileUnits);
          if (rawData?.length > 0) {
            setFBAData(rawData);
            setFBAProcessedData(rawData);
            setFBAProductionFileName(fileName);
            setFBAProductionFileSize(fileSize);
          };
        };

      };
    } catch (error) {
      console.log(error);
    };
  };

  function handleProductionFileUnitsChange(e, value) {
    value && setFBAProductionFileUnits(value);
  };
  function handleProductionFilePressureTypeChange(e, value) {
    value && setFBAProductionFilePressureType(value);
  };
  function handleFracTypeChange(e, value) {
    try {
      if (!value) return;
      setFBAFracType(value);
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function applySmoothing(data, newSmoothingWindow) {
    try {
      let newPreviewData = deepCopy(data);

      if (newSmoothingWindow > 0) {
        console.log('Applying SMA with window size of ' + newSmoothingWindow);

        const paddingCount = Math.floor(Math.max((newSmoothingWindow - 1) / 2, 0));

        for (const trace of ['oil', 'condensate', 'gas', 'water', 'pwh', 'pwf']) {
          
          const traceData = [...data]?.map(d => getTraceData(d, trace, fbaProductionFileUnits));

          let smoothedTraceData = [...sma(traceData, newSmoothingWindow, n => parseFloat(n))];
          
          // Apply padding to the beginning and the end of the array
          smoothedTraceData = traceData.slice(0, paddingCount)?.concat(smoothedTraceData);
          if (paddingCount > 0) {
            smoothedTraceData = smoothedTraceData?.concat(traceData.slice(-paddingCount));
          };
    
          for (let i = 0; i < smoothedTraceData?.length; i++) {
            const row = smoothedTraceData[i];
            newPreviewData[i][trace] = row;
          };
        };
      };

      return newPreviewData;      
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function applyDespiking(data, newDespikingSensitivity, newDespikingWindow) {
    try {
      let newPreviewData = deepCopy(data);

      if (newDespikingWindow > 0 && newDespikingSensitivity > 0) {
        console.log(`Applying de-spiking with sensitivity of ${newDespikingSensitivity} and window of ${newDespikingWindow}`);

        for (const trace of ['oil', 'condensate', 'gas', 'water', 'pwh', 'pwf']) {

          const traceData = data?.map(d => getTraceData(d, trace, fbaProductionFileUnits));
          let smoothedTraceData = [...sma(traceData, newDespikingWindow, n => parseFloat(n))];
          
          let iteration = 1;
          while (smoothedTraceData.length < traceData.length) {
            if (iteration % 2 !== 0) {
              smoothedTraceData.unshift(traceData[0]);
            } else {
              smoothedTraceData.push(traceData[traceData.length - 1]);
            };
            iteration++;
          };
  
          // calculate local std dev
          const stdDev = Math.sqrt(traceData.map((d,i) => Math.pow((d - smoothedTraceData[i]), 2)).reduce((partial, el) => partial + el, 0) / traceData.length);
          const threshold = newDespikingSensitivity * stdDev;
          
          const despikedData = [];
          
          for (let i = 0; i < traceData.length; i++) {
            let value = traceData[i];
            if ((value > (smoothedTraceData[i] + threshold) || (value < (smoothedTraceData[i] - threshold))) && i > 1 && (i < traceData.length - 2)) {
              value = (traceData[i - 2] + traceData[i - 1] + traceData[i + 1] + traceData[i + 2]) / 4;
            };
            despikedData.push(value);
          };
  
          for (let i = 0; i < despikedData?.length; i++) {
            const row = despikedData[i];
            newPreviewData[i][trace] = row;
          };
        };
      };

      return newPreviewData;      
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function applyInterpolation(data, newInterpolationFrequency) {
    try {
      let newPreviewData = deepCopy(data);

      const interpolatedData = [];

      if (newInterpolationFrequency === 0) {
        return newPreviewData;
      } else {
        console.log(`Applying interpolation with frequency of ${newInterpolationFrequency}`);

        for (let i = 0; i < newPreviewData.length - 1; i++) {
          const currentData = newPreviewData[i];
          const nextData = newPreviewData[i + 1];
          interpolatedData.push(currentData);
      
          const timeDiff = (new Date(nextData.date)?.getTime() - new Date(currentData.date)?.getTime()) / (newInterpolationFrequency + 1);
          const daysValueDiff = (nextData.days - currentData.days) / (newInterpolationFrequency + 1);
          const oilValueDiff = (nextData.oil - currentData.oil) / (newInterpolationFrequency + 1);
          const gasValueDiff = (nextData.gas - currentData.gas) / (newInterpolationFrequency + 1);
          const condensateValueDiff = (nextData.condensate - currentData.condensate) / (newInterpolationFrequency + 1);
          const waterValueDiff = (nextData.water - currentData.water) / (newInterpolationFrequency + 1);
          const pwhValueDiff = (nextData.pwh - currentData.pwh) / (newInterpolationFrequency + 1);
          const pwfValueDiff = (nextData.pwf - currentData.pwf) / (newInterpolationFrequency + 1);
          const salinityValueDiff = (nextData.salinity - currentData.salinity) / (newInterpolationFrequency + 1);
      
          for (let j = 1; j <= newInterpolationFrequency; j++) {
            const interpolatedDate = new Date(new Date(currentData.date).getTime() + timeDiff * j);
            const daysInterpolatedValue = currentData.days + daysValueDiff * j;
            const oilInterpolatedValue = currentData.oil > 0.0001 ? (currentData.oil + oilValueDiff * j) : currentData.oil;
            const gasInterpolatedValue = currentData.gas > 0.0001 ? (currentData.gas + gasValueDiff * j) : currentData.gas;
            const condensateInterpolatedValue = currentData.condensate > 0.0001 ? (currentData.condensate + condensateValueDiff * j) : currentData.condensate;
            const waterInterpolatedValue = currentData.water > 0.0001 ? (currentData.water + waterValueDiff * j) : currentData.water;
            const pwhInterpolatedValue = currentData.pwh + pwhValueDiff * j;
            const pwfInterpolatedValue = currentData.pwf + pwfValueDiff * j;
            const salinityInterpolatedValue = currentData.salinity + salinityValueDiff * j;
            interpolatedData.push({ 
              date: interpolatedDate?.toLocaleString(), 
              days: daysInterpolatedValue, 
              oil: oilInterpolatedValue,
              gas: gasInterpolatedValue,
              condensate: condensateInterpolatedValue,
              water: waterInterpolatedValue,
              pwh: pwhInterpolatedValue,
              pwf: pwfInterpolatedValue,
              salinity: salinityInterpolatedValue
             });
          };
        };
      
        interpolatedData.push(newPreviewData[newPreviewData.length - 1]);
      
      };
      

      // calculate additional fields
      interpolatedData.forEach((d, i) => {
        const delta = i === 0 ? d.days : (d.days - interpolatedData[i-1].days); // calculate delta in days
        interpolatedData[i].sqrtDays = Math.sqrt(interpolatedData[i].days); // calculate sqrt(t)
        interpolatedData[i].gor = (d.oil > 0.0001 && d.gas > 0.0001) ? ((1000 * d.gas) / d.oil) : 0; // calculate gor
        interpolatedData[i].owr = (d.water > 0.0001 && d.oil > 0.0001) ? (d.oil / d.water) : 0; // calculate owr
        interpolatedData[i].gwr = (d.water > 0.0001 && d.gas > 0.0001) ? (d.gas / d.water) : 0; // calculate gwr
        interpolatedData[i].cgr = (d.condensate > 0.0001 && d.gas > 0.0001) ? (d.condensate / (d.gas / 1000)) : 0; // calculate cgr
        interpolatedData[i].cumWater = (d.water > 0 ? d.water * (delta) : 0.00001) + (i > 0 ? interpolatedData?.[i-1]?.cumWater : 0) // calculate cum water
        interpolatedData[i].cumOil = (d.oil > 0 ? d.oil * (delta) : 0.00001) + (i > 0 ? interpolatedData?.[i-1]?.cumOil : 0) // calculate cum oil
        interpolatedData[i].cumGas = (d.gas > 0 ? d.gas * (delta) : 0.00001) + (i > 0 ? interpolatedData?.[i-1]?.cumGas : 0) // calculate cum gas
        interpolatedData[i].cumCondensate = (d.condensate > 0 ? d.condensate * (delta) : 0.00001) + (i > 0 ? interpolatedData?.[i-1]?.cumCondensate : 0); // calculate cum condensate
      });

      return interpolatedData;
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function applyRemoveOutliers(data, newLOFKNeighbors, newLOFCutoff, newMMRKNeighbors, newMMRCutoff) {
    try {
      let newPreviewData = deepCopy(data);

      if ((newLOFKNeighbors > 0 || newMMRKNeighbors > 0) && (newLOFCutoff > 0 || newMMRCutoff > 0)) {
        console.log(`Removing outliers with LOF k of ${newLOFKNeighbors}, LOF cutoff of ${newLOFCutoff}, MMR k of ${newMMRKNeighbors} and MMR cutoff of ${newMMRCutoff}`);

        for (const trace of ['oil', 'condensate', 'gas', 'water', 'pwh', 'pwf']) {

          const traceData = removeOutliersFBAData(data, trace, newLOFKNeighbors, newLOFCutoff, newMMRKNeighbors, newMMRCutoff);

          for (let i = 0; i < traceData?.length; i++) {
            const row = traceData[i];
            newPreviewData[i][trace] = row;
          };
        };
      };

      return newPreviewData; 

    } catch (error) {
      console.log(error);
    };
  };

  function recalculateFluidRatios(data) {
    try {
      if (!data || data?.length === 0) return [];

      const fbaProductionData = JSON.parse(JSON.stringify(data)) || []; 

      fbaProductionData.forEach((d,i) => {
        const delta = i === 0 ? d.days : (d.days - fbaProductionData[i-1].days); // calculate delta in days

        fbaProductionData[i].gor = (d.oil > 0.0001 && d.gas > 0.0001) ? ((1000 * d.gas) / d.oil) : 0; // calculate gor
        fbaProductionData[i].owr = (d.water > 0.0001 && d.oil > 0.0001) ? (d.oil / d.water) : 0; // calculate owr
        fbaProductionData[i].gwr = (d.water > 0.0001 && d.gas > 0.0001) ? (d.gas / d.water) : 0; // calculate gwr
        fbaProductionData[i].cgr = (d.condensate > 0.0001 && d.gas > 0.0001) ? (d.condensate / (d.gas / 1000)) : 0; // calculate cgr
        fbaProductionData[i].cumWater = (d.water > 0 ? d.water * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumWater : 0) // calculate cum water
        fbaProductionData[i].cumOil = (d.oil > 0 ? d.oil * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumOil : 0) // calculate cum oil
        fbaProductionData[i].cumGas = (d.gas > 0 ? d.gas * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumGas : 0) // calculate cum gas
        fbaProductionData[i].cumCondensate = (d.condensate > 0 ? d.condensate * (delta) : 0.00001) + (i > 0 ? fbaProductionData?.[i-1]?.cumCondensate : 0); // calculate cum condensate
      });

      return fbaProductionData;

    } catch (error) {
      console.log(error);
      return [];
    };
  };

  function getTraceData(data, trace, units) {
    switch(trace) {
      case 'oil':
        return data.oil;
        // return (units === 'metric' ? data.oil / M3_TO_BBL : data.oil);
      case 'condensate':
        return data.condensate;
        // return (units === 'metric' ? data.condensate / M3_TO_BBL : data.condensate);
      case 'gas':
        return data.gas;
        // return (units === 'metric' ? data.gas / E3M3_TO_MCF : data.gas);
      case 'water':
        return data.water;
        // return (units === 'metric' ? data.water / M3_TO_BBL : data.water);
      case 'pwh':
        return data.pwh;
      case 'pwf':
        return data.pwf;
      default:
        return 0;
    };
  };


  function resetAugmentationParameters() {
    setSmoothingWindow(0);
    setDespikingSensitivity(0);
    setDespikingWindow(0);
    setLOFKNeighbors(0);
    setLOFCutoff(0);
    setMMRCutoff(0);
    setMMRKNeighbors(0);
    setInterpolationFrequency(0);
  };

  function deepCopy(obj) {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      console.log(error);
      return obj;
    };
  };
};

function FBAEditSurveyData({
  fbaSurveyData, 
  setFBASurveyData, 
  fbaSurveyFileUnits,
  setFBASurveyFileUnits,
  fbaSurveyFileName,
  setFBASurveyFileName,
  fbaSurveyFileSize,
  setFBASurveyFileSize,
  darkMode
 }) {
  if (!fbaSurveyData) {
    return (
      <Box>
        <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 10, marginBottom: 10}}>
          <FormLabel component="legend">Survey Data Units</FormLabel>
          <ToggleButtonGroup
            value={fbaSurveyFileUnits}
            exclusive
            onChange={handleSurveyFileUnitsChange}
            size="small"
            style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center'}}
          >
            <ToggleButton value="metric" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
              <Typography style={{fontSize: 12}}>
                Metric
              </Typography>
            </ToggleButton>
            <ToggleButton value="field" size="small" style={{width: '50%'}} color={darkMode ? "" : "primary"}>
              <Typography style={{fontSize: 12}}>
                Field
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
        <div style={{width: 'calc(100% - 20px)', height: 100, marginTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Button
            component="label"
            variant="contained" 
            color={(darkMode ? "inherit" : "primary")}
            style={{color: (darkMode ? '#000' : '#fff'), height: 28, marginBottom: 5}} 
            startIcon={<UploadFile />}
            size="small"
          >
            Upload Survey Data
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={e => handleUploadFBASurveyData(e.target.files)}
            />
          </Button>
        </div>
      </Box>
    );
  } else {
    return (
      <div>
        <div style={{marginTop: 20, display: 'flex', justifyContent: 'space-between'}}>
          <div>
            <Typography fontSize={12}>File: {fbaSurveyFileName || ""}</Typography>
            <Typography fontSize={12}>Size: {fbaSurveyFileSize?.toFixed(1) || 0} kb</Typography>
            <Typography fontSize={12}>Rows: {fbaSurveyData?.length || 0}</Typography>
            <Typography fontSize={12}>Units: {fbaSurveyFileUnits?.charAt(0)?.toUpperCase()?.concat(fbaSurveyFileUnits?.substr(1, ))}</Typography>
          </div>
          <Button 
            startIcon={<Delete fontSize="20px" />} 
            onClick={() => {
              setFBASurveyData(null);
            }}
            variant="contained" 
            color={(darkMode ? "inherit" : "secondary")}
            style={{color: (darkMode ? '#000' : '#fff'), height: 28}}
            size="small"
          >
            Delete Data
          </Button>
        </div>

        <FBASurveyDataTable fbaSurveyData={fbaSurveyData} units={fbaSurveyFileUnits} />
      </div>
    )
  };

  function handleSurveyFileUnitsChange(e, value) {
    value && setFBASurveyFileUnits(value);
  };

  async function handleUploadFBASurveyData(inputData) {
    try {
      if (inputData) {
        const {fbaSurveyData, fileName, fileSize} = await parseFbaSurveyCsv(inputData, fbaSurveyFileUnits);
        if (!fbaSurveyData) return;

        setFBASurveyData(() => fbaSurveyData);
        setFBASurveyFileName(fileName);
        setFBASurveyFileSize(fileSize);
      };
    } catch (error) {
      console.log(error);
    };
  };

  // function handleDeleteSurveyData() {
  //   setFBASurveyData(null);
  //   setFBASurveyFileName(null);
  //   setFBASurveyFileSize(null);
  // };
};